import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TextareaModule } from 'primeng/textarea';
import { SilenciadorDeErrosEmLote } from 'src/handlers/error/parsers/ignore-message';

import { PermissionService } from '@/core/permissions/permission.service';
import { LoggerService } from '@/core/services/logger.service';
import { DashboardServicosFacade } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.facade';
import { type TipoServicoPessoa } from '@/features/servicos/shared/enums/servico-disponivel.enum';
import { type KanbanColuna, chaveProtocolo } from '@/features/servicos/shared/kanban-protocolos/kanban-protocolos';
import type { ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { separarCrmUf } from '@/features/servicos/shared/utils/crm.utils';
import {
    type AcaoServico,
    acoesOcultasPorUf,
    campoDaConfirmacao,
    crmUfDoRostoDoCard,
    efetivacaoSoNaTelaDeDetalheNoLote,
    identificacaoPorCnpj,
    rolesDoServico,
    rostoDoCardEhDoDiretorTecnico
} from '@/features/servicos/shared/utils/servicos-acoes.utils';
import { formatarDataTramitacao, identificadorProtocolo } from '@/features/servicos/shared/utils/protocolo-rotulos.utils';
import { FotosMedicosService, iniciaisDoNome } from '@/shared/services/fotos-medicos.service';
import { MascaraUtil } from '@/shared/utils/mascara-util';

import { EEstadoDaFila, EEstadoDoItemDoLote, EMotivoDaParada } from './encerramento-lote.model';
import { EncerramentoLoteService } from './encerramento-lote.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/**
 * Por que um protocolo marcado pelo analista NÃO entra no lote. Conjunto fechado, e por isso `enum`:
 * cada valor é lido em dois lugares (a linha do item e o agregado do rodapé), e um literal solto
 * faria os dois divergirem.
 */
export enum EMotivoForaDoLote {
    SEM_IDENTIDADE = 'sem-identidade',
    ACAO_OCULTA_POR_UF = 'acao-oculta-por-uf',
    SEM_PERMISSAO = 'sem-permissao',
    SO_NA_TELA_DE_DETALHE = 'so-na-tela-de-detalhe',
    /** A efetivação deste serviço exige um campo por protocolo (data ou observação), e o lote não coleta campo nenhum. */
    EXIGE_CAMPO_NA_CONFIRMACAO = 'exige-campo-na-confirmacao',
    SEM_ENDPOINT = 'sem-endpoint',
    SEM_PAGAMENTO = 'sem-pagamento'
}

/**
 * Dois textos por motivo, não um: a linha explica ao analista o que houve com AQUELE protocolo; o
 * agregado precisa caber numa frase contada ("12 de 20 fora por pagamento"), e a frase da linha lida
 * ali sairia sem sentido.
 */
const TEXTOS_DO_MOTIVO: Record<EMotivoForaDoLote, { linha: string; resumo: string }> = {
    [EMotivoForaDoLote.SEM_IDENTIDADE]: { linha: 'Protocolo sem número/ano válidos.', resumo: 'número/ano ausentes' },
    [EMotivoForaDoLote.ACAO_OCULTA_POR_UF]: { linha: 'Ação indisponível na UF deste protocolo.', resumo: 'UF' },
    [EMotivoForaDoLote.SEM_PERMISSAO]: { linha: 'Sem permissão para esta ação neste serviço.', resumo: 'permissão' },
    [EMotivoForaDoLote.SO_NA_TELA_DE_DETALHE]: { linha: 'Este serviço só efetiva pela tela de detalhe.', resumo: 'efetivação exclusiva da tela de detalhe' },
    [EMotivoForaDoLote.EXIGE_CAMPO_NA_CONFIRMACAO]: { linha: 'A efetivação deste serviço exige um campo por protocolo, que o lote não coleta.', resumo: 'campo obrigatório na efetivação' },
    [EMotivoForaDoLote.SEM_ENDPOINT]: { linha: 'Serviço sem endpoint desta ação mapeado.', resumo: 'serviço sem endpoint mapeado' },
    [EMotivoForaDoLote.SEM_PAGAMENTO]: { linha: 'Protocolo sem pagamento confirmado.', resumo: 'pagamento' }
};

/** Um protocolo já julgado pela elegibilidade do lote: `motivo` nulo é quem vai. */
export interface ItemDaConferencia {
    chave: string;
    item: ItemStatus;
    motivo: EMotivoForaDoLote | null;
}

/** Quantos protocolos caíram pelo MESMO motivo, para a frase agregada do rodapé. */
export interface AgregadoForaDoLote {
    motivo: EMotivoForaDoLote;
    quantidade: number;
}

/** A partir de quantos itens no mesmo motivo o agregado vale a pena: com um só, a linha dele já diz tudo. */
const MINIMO_PARA_AGREGAR = 2;

/**
 * Como cada desfecho se mostra na linha. **Os TRÊS desfechos finais são visualmente distintos**, e
 * isso não é enfeite: `CONCLUIDO_COM_PENDENCIAS` significa que a efetivação FUNCIONOU e o CRM existe
 * — pintá-lo de sucesso esconderia o trabalho de cadastro que ficou; pintá-lo de falha faria o
 * analista retentar o que já deu certo, e a repetição de uma finalização já aceita é exatamente o que
 * a fila inteira existe para nunca fazer.
 */
const APRESENTACAO_DO_ESTADO: Record<EEstadoDoItemDoLote, { rotulo: string; classe: string; icone: string }> = {
    [EEstadoDoItemDoLote.PENDENTE]: { rotulo: 'Na fila', classe: 'lote-estado-marca lote-estado-pendente', icone: 'pi pi-clock' },
    [EEstadoDoItemDoLote.EXECUTANDO]: { rotulo: 'Executando', classe: 'lote-estado-marca lote-estado-executando', icone: 'pi pi-spin pi-spinner' },
    [EEstadoDoItemDoLote.CONCLUIDO]: { rotulo: 'Encerrado', classe: 'lote-estado-marca lote-estado-concluido', icone: 'pi pi-check-circle' },
    [EEstadoDoItemDoLote.CONCLUIDO_COM_PENDENCIAS]: { rotulo: 'Encerrado com pendência de cadastro', classe: 'lote-estado-marca lote-estado-com-pendencias', icone: 'pi pi-exclamation-triangle' },
    [EEstadoDoItemDoLote.FALHOU]: { rotulo: 'Falhou', classe: 'lote-estado-marca lote-estado-falhou', icone: 'pi pi-times-circle' }
};

/**
 * O passo em que o item está DENTRO da execução, deduzido de `aceitoNoPortal` — o único dado que a
 * fila publica sobre o meio do caminho. Importa porque os dois passos têm consequências diferentes se
 * algo der errado: antes do portal aceitar, nada foi feito; depois, a solicitação já está finalizada
 * lá e só falta o quadro.
 */
const PASSO_FINALIZANDO = 'Finalizando no portal';
const PASSO_MOVENDO = 'Gravando o movimento no quadro';

/**
 * O item que o portal JÁ aceitou e cujo movimento não foi gravado no quadro. Não é "falhou" e não é
 * "encerrado": é o único estado do lote em que os dois lados discordam, e ele fica invisível assim que
 * o quadro recarregar — o protocolo sai da listagem viva (a situação deixou de ser A/T) e não entra na
 * coluna Concluído (a tabela de controle não foi movida). Por isso ele tem rótulo próprio: pintá-lo de
 * "Falhou" faria o analista acreditar que nada foi feito no portal, que é o contrário do que houve.
 */
const ROTULO_PRESO = 'Finalizado no portal, sem movimento no quadro';
const CLASSE_PRESO = 'lote-estado-marca lote-estado-preso';
const ICONE_PRESO = 'pi pi-link';

/** O que aconteceu com o último pedido de cópia — some da tela nenhuma feedback é pior que feedback errado. */
export enum EResultadoDaCopia {
    NAO_PEDIDA = 'nao-pedida',
    COPIADO = 'copiado',
    FALHOU = 'falhou'
}

const TEXTOS_DA_COPIA: Record<EResultadoDaCopia, string> = {
    [EResultadoDaCopia.NAO_PEDIDA]: '',
    [EResultadoDaCopia.COPIADO]: 'Resultado copiado.',
    [EResultadoDaCopia.FALHOU]: 'Não foi possível copiar. Selecione o texto da lista e copie manualmente.'
};

/**
 * Uma linha da tabela do diálogo, nas DUAS fases: a mesma lista, nas mesmas posições, que ganha uma
 * coluna de estado quando o lote dispara. Não é uma tela nova de propósito — o analista continua
 * vendo as mesmas linhas, o que torna óbvio qual protocolo está onde.
 *
 * `estado` nulo é a fase de conferência: ali não existe estado por item, e mostrar "pendente" antes
 * de o lote começar sugeriria que algo já está em curso.
 */
export interface LinhaDoLote {
    chave: string;
    item: ItemStatus;
    estado: EEstadoDoItemDoLote | null;
    rotulo: string;
    classe: string;
    icone: string;
    erro: string | null;
    pendencias: string[];
    /** Item ainda não começado: é o único que pode sair do lote depois do disparo. */
    removivel: boolean;
    /** Falhou DEPOIS de o portal aceitar: o protocolo está finalizado lá e parado aqui — ver `ROTULO_PRESO`. */
    preso: boolean;
}

/**
 * O encerramento em lote inteiro, do lado do analista: primeiro a conferência dos N protocolos
 * marcados, depois o acompanhamento item a item e o resumo do que aconteceu.
 *
 * A conferência existe porque encerrar é irreversível e o fluxo card a card nunca mostra o conjunto —
 * é aqui que se pega o card marcado por engano, o que veio de um filtro esquecido e o que ainda não
 * deveria ser encerrado. Por isso **nada é chamado até o analista confirmar**: abrir e sair daqui não
 * tem consequência nenhuma.
 *
 * Depois do disparo, o diálogo é a ÚNICA superfície que mostra o que a fila está fazendo, e é por
 * isso que ele **nunca se fecha sozinho**: fechar ao terminar levaria embora o resumo e a lista de
 * pendências, que é justamente de onde sai o trabalho de correção do dia seguinte.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-encerramento-lote',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, DialogModule, ButtonModule, TextareaModule],
    /**
     * Cache de retratos próprio, como o arquivo de protocolos faz: o serviço não é `providedIn: 'root'`,
     * e um lote é uma população pequena e fechada (o que o analista marcou), buscada de uma vez em
     * `abrir()` — diferente do quadro, que carrega foto por card à medida que rolam à vista porque o
     * backlog pode ter centenas.
     */
    providers: [FotosMedicosService],
    templateUrl: './encerramento-lote.html',
    styleUrl: './encerramento-lote.scss'
})
export class EncerramentoLote {
    private readonly facade = inject(DashboardServicosFacade);
    private readonly permissionService = inject(PermissionService);
    private readonly logger = inject(LoggerService);
    /**
     * A fila vive num serviço provido pelo `dashboard-servicos`, e não aqui: ela sobrevive à
     * destruição do quadro. Na conferência ela é só a porta que o `fechar()` precisa trancar — nenhum
     * método que toque o portal é chamado nessa fase; quem a acorda é `confirmar()`.
     */
    private readonly fila = inject(EncerramentoLoteService);
    /**
     * A janela em que o toast global se cala para os endpoints do lote. O diálogo já lista o erro real
     * de cada item; sem a janela, uma fila com dez falhas empilharia dez toasts POR CIMA dessa lista.
     * Aberta em `confirmar()` e fechada em `fechar()` — e também na destruição, para uma janela nunca
     * sobreviver ao componente que a abriu.
     */
    private readonly silenciadorDeErros = inject(SilenciadorDeErrosEmLote);
    /** Retratos dos médicos do lote — ver a nota dos `providers` sobre por que o cache é deste diálogo. */
    private readonly fotosMedicos = inject(FotosMedicosService);

    /** Código do serviço aberto no quadro; é o eixo de quase todos os gates de elegibilidade. */
    readonly codigoServico = input('');
    /** UF do quadro, usada como fallback quando o protocolo não traz a própria (`ufDoProtocolo` do quadro). */
    readonly uf = input('');
    /**
     * Pessoa física ou jurídica. Decide de QUEM é o rosto de cada linha: na jurídica o retratado é o
     * diretor técnico, e não a empresa cujo nome se lê ao lado — a mesma regra do card do quadro.
     */
    readonly tipoPessoa = input<TipoServicoPessoa>('pessoa-fisica');
    /**
     * Id do serviço (`EServico`), que o movimento de cada item precisa carregar. `undefined` quando o
     * código da rota não está no catálogo — e aí não há lote possível, porque `MoverSolicitacaoRequest`
     * exige o id.
     */
    readonly idServico = input<number | undefined>(undefined);

    /**
     * O diálogo fechou depois de um lote que chegou a rodar — o único momento em que o quadro precisa
     * ser relido, e UMA vez só.
     *
     * Não ao terminar a fila, e nunca por item: cada recarga do quadro são ~10 chamadas, e
     * `/solicitacoes/concluidas` consulta o portal uma vez por concluído — lista que a própria fila faz
     * crescer, o que torna a recarga por item quadrática no tamanho do lote. Pior: a recarga destrói o
     * `KanbanProtocolos`, e uma fila que morasse lá morreria no primeiro item.
     *
     * Não emite quando o lote não foi disparado: abrir a conferência e desistir não mudou nada no
     * servidor, e recarregar ali cobraria as ~10 chamadas E apagaria a seleção do analista
     * (`carregar()` chama `limparSelecionados()`) — punindo justamente quem usou o diálogo como
     * conferência, que é para o que ele existe.
     */
    readonly recargaDoQuadroSolicitada = output<void>();

    private readonly visivelSignal = signal(false);
    /** O diálogo está aberto. */
    readonly visivel = this.visivelSignal.asReadonly();

    private readonly selecaoSignal = signal<ItemStatus[]>([]);
    private readonly removidasSignal = signal<ReadonlySet<string>>(new Set<string>());

    private readonly colunaSignal = signal<KanbanColuna | null>(null);
    /** Coluna de onde a seleção saiu. Uma só para o lote inteiro: o botão só aparece na coluna dona da seleção. */
    readonly colunaDaSelecao = this.colunaSignal.asReadonly();

    /** Coluna conclusiva para onde cada protocolo vai depois de finalizado no portal — vem do quadro, único lugar que a conhece. */
    private readonly colunaDestinoSignal = signal<KanbanColuna | null>(null);

    /**
     * Este diálogo já mandou a fila começar. **Não é `estadoDaFila() !== CONFERINDO`**: o serviço é
     * provido pelo `dashboard-servicos` e sobrevive ao fechamento do diálogo, então na segunda abertura
     * ele ainda está `ENCERRADA`, cheio dos itens do lote anterior — ler o estado da fila para decidir
     * a fase mostraria o resultado antigo por cima da conferência nova.
     */
    private readonly disparadoSignal = signal(false);
    readonly disparado = this.disparadoSignal.asReadonly();

    /**
     * O analista pediu para PARAR este lote, e não a sessão que caiu. Lido do serviço, que é quem
     * decide os dois casos: um espelho local aqui seria uma segunda verdade sobre o mesmo fato e
     * divergiria na primeira parada que não tivesse saído deste diálogo.
     */
    private readonly paradaPedidaPeloAnalista = computed(() => this.fila.motivoDaParada() === EMotivoDaParada.PEDIDO_DO_ANALISTA);

    private readonly copiaSignal = signal<EResultadoDaCopia>(EResultadoDaCopia.NAO_PEDIDA);
    /** Feedback da última cópia; vazio enquanto ninguém pediu. */
    readonly mensagemDaCopia = computed(() => TEXTOS_DA_COPIA[this.copiaSignal()]);

    /**
     * Efetivar OU cancelar, uma só para o lote inteiro (decisão do usuário: não há escolha por
     * protocolo). Nasce em `efetivar` porque a lista de conferência precisa de uma ação para ser
     * calculada já na abertura — sem default, o analista veria a tela vazia antes de tocar no rádio, e
     * a conferência começaria depois da decisão em vez de antes dela.
     */
    private readonly acaoSignal = signal<AcaoServico>('efetivar');
    readonly acao = this.acaoSignal.asReadonly();

    private readonly motivoSignal = signal('');
    readonly motivo = this.motivoSignal.asReadonly();

    /** O cancelamento vai ao portal com um motivo; a efetivação não tem onde colocá-lo. */
    readonly exigeMotivo = computed(() => this.acaoSignal() === 'cancelar');

    /**
     * A seleção inteira, já julgada — e recalculada a cada troca do rádio, porque efetivar e cancelar
     * têm gates DIFERENTES: a mesma seleção produz listas diferentes. É por isso que a ação vem antes
     * da lista na tela.
     */
    private readonly conferencia = computed<ItemDaConferencia[]>(() => {
        const acao = this.acaoSignal();
        const removidas = this.removidasSignal();
        return this.selecaoSignal()
            .map((item) => ({ chave: chaveProtocolo(item), item, motivo: this.motivoForaDoLote(item, acao) }))
            .filter((avaliado) => !removidas.has(avaliado.chave));
    });

    /** Os que a fila vai processar. */
    readonly itensQueVao = computed(() => this.conferencia().filter((avaliado) => avaliado.motivo === null));

    /** Os que ficam de fora, cada um com o seu motivo. */
    readonly foraDoLote = computed(() => this.conferencia().filter((avaliado) => avaliado.motivo !== null));

    /**
     * Motivos que derrubaram vários protocolos de uma vez. `inPago` é opcional no DTO e o gate é
     * `!== true`, então campo ausente cai como "sem pagamento" — num lote grande isso esvazia a fila
     * sem que a causa real seja pagamento, e sem o agregado o analista leria N linhas iguais sem
     * perceber que são a MESMA coisa.
     */
    readonly agregadosForaDoLote = computed<AgregadoForaDoLote[]>(() => {
        const contagem = new Map<EMotivoForaDoLote, number>();
        for (const avaliado of this.foraDoLote()) {
            if (avaliado.motivo !== null) {
                contagem.set(avaliado.motivo, (contagem.get(avaliado.motivo) ?? 0) + 1);
            }
        }
        return [...contagem.entries()].filter(([, quantidade]) => quantidade >= MINIMO_PARA_AGREGAR).map(([motivo, quantidade]) => ({ motivo, quantidade }));
    });

    /** Total conferido (dentro + fora), que é o denominador das frases agregadas. */
    readonly totalConferido = computed(() => this.conferencia().length);

    /**
     * Nada a disparar sem ao menos um protocolo elegível — e, no cancelamento, sem o motivo que vai ao
     * portal. Também não há segundo disparo do MESMO lote: `iniciar()` reconstrói a fila do zero, e um
     * segundo clique reenviaria ao portal finalizações que ele já aceitou. Sem `idServico` não há
     * movimento a gravar — o item terminaria finalizado no portal e parado no quadro, que é o pior
     * dos estados.
     *
     * <p>Rodada de correção 2 (Menor 8): o `colunaDestinoSignal() !== null` que morava aqui era
     * guarda morta e saiu. `colunaDestino` é parâmetro OBRIGATÓRIO e não-nulo de `abrir()`, único
     * lugar que povoa tanto a seleção quanto o destino: antes dele `itensQueVao()` já é vazio e a
     * primeira condição barra sozinha; depois dele o sinal nunca volta a ser nulo. A guarda de
     * `confirmar()` continua onde está, e não é a mesma coisa — lá ela é o estreitamento de tipo que
     * o `IniciarLoteConfig` exige, num método público que o botão não é a única porta de entrada.</p>
     */
    readonly podeConfirmar = computed(() => !this.disparado() && this.itensQueVao().length > 0 && this.idServico() !== undefined && (!this.exigeMotivo() || this.motivoSignal().trim().length > 0));

    /** Onde a fila está, para a tela. Lido do serviço: um espelho local aqui divergiria dele na primeira parada por 401. */
    readonly estadoDaFila = this.fila.estadoDaFila;

    /**
     * Os quatro números do lote, direto do serviço. **Nunca recontados a partir das linhas da tela**:
     * item retirado do lote sai do array de itens, e só o serviço lembra que ele fazia parte do total.
     */
    readonly resumo = this.fila.resumo;

    /**
     * "X encerrados" do resumo — os concluídos SEM pendência. `resumo().concluidos` do serviço soma os
     * dois desfechos bem-sucedidos (é o contrato dele, documentado no model), e exibi-lo ao lado de
     * "W com pendências" contaria o mesmo protocolo duas vezes: quatro números que somassem mais que o
     * total fariam o analista desconfiar do único lugar que lhe diz o que aconteceu.
     */
    readonly encerrados = computed(() => this.resumo().concluidos - this.resumo().comPendencias);

    /**
     * Quantos itens a fila já resolveu, de um jeito ou de outro. Os TRÊS desfechos terminais entram:
     * deixar os presos de fora (Importante 6 separou-os de `falharam`) faria o contador de um lote com
     * um preso nunca fechar — "3 de 4" com a fila parada e nada mais por vir.
     */
    readonly processados = computed(() => this.resumo().concluidos + this.resumo().falharam + this.resumo().presos);

    /** Denominador do contador: o que ainda está no lote — quem saiu dele não vai ser processado nunca. */
    readonly totalNaFila = computed(() => this.resumo().total - this.resumo().foraDoLote);

    /** "4 de 14": o contador que diz que a fila está viva e onde ela está. */
    readonly progresso = computed(() => `${this.processados()} de ${this.totalNaFila()}`);

    /**
     * A mesma lista da conferência, agora com a coluna de estado. Antes do disparo vem da conferência
     * (nenhum estado por item); depois vem da fila, que é quem sabe o desfecho de cada um.
     */
    readonly linhas = computed<LinhaDoLote[]>(() => {
        if (!this.disparado()) {
            return this.itensQueVao().map((avaliado) => ({
                chave: avaliado.chave,
                item: avaliado.item,
                estado: null,
                rotulo: '',
                classe: '',
                icone: '',
                erro: null,
                pendencias: [],
                removivel: true,
                preso: false
            }));
        }

        return this.fila.itens().map((doLote) => {
            const apresentacao = APRESENTACAO_DO_ESTADO[doLote.estado];
            // Falhou DEPOIS do portal aceitar: o desfecho é o de `ROTULO_PRESO`, não o de "Falhou".
            const preso = doLote.estado === EEstadoDoItemDoLote.FALHOU && doLote.aceitoNoPortal;
            return {
                chave: doLote.chave,
                item: doLote.item,
                estado: doLote.estado,
                rotulo: this.rotuloDaLinha(doLote.estado, doLote.aceitoNoPortal, preso, apresentacao.rotulo),
                classe: preso ? CLASSE_PRESO : apresentacao.classe,
                icone: preso ? ICONE_PRESO : apresentacao.icone,
                erro: doLote.erro,
                pendencias: doLote.pendencias,
                removivel: doLote.estado === EEstadoDoItemDoLote.PENDENTE,
                preso
            };
        });
    });

    /** Rótulo da linha: o passo enquanto executa, o aviso de item preso quando o portal já aceitou, e o rótulo do estado no resto. */
    private rotuloDaLinha(estado: EEstadoDoItemDoLote, aceitoNoPortal: boolean, preso: boolean, rotuloDoEstado: string): string {
        if (estado === EEstadoDoItemDoLote.EXECUTANDO) {
            return aceitoNoPortal ? PASSO_MOVENDO : PASSO_FINALIZANDO;
        }
        return preso ? ROTULO_PRESO : rotuloDoEstado;
    }

    /**
     * Os encerrados que deixaram cadastro incompleto. É a lista que **sobrevive ao fechamento** do
     * diálogo no sentido que importa: dela sai a fila de correção manual, e por isso ela é copiável.
     */
    readonly linhasComPendencias = computed(() => this.linhas().filter((linha) => linha.estado === EEstadoDoItemDoLote.CONCLUIDO_COM_PENDENCIAS));

    /**
     * Os que não foram encerrados E não chegaram a sair daqui: nada foi feito no portal, repetir refaz
     * a dupla completa. Os presos ficam de fora desta lista de propósito — juntar os dois esconderia a
     * única diferença que muda a conduta do analista (ver `linhasPresas`).
     */
    readonly linhasQueFalharam = computed(() => this.linhas().filter((linha) => linha.estado === EEstadoDoItemDoLote.FALHOU && !linha.preso));

    /**
     * Os presos entre o portal e o quadro. Lista própria porque é a única do lote que tem prazo: com o
     * quadro recarregado, o card some da tela (ver `ROTULO_PRESO`) e a recuperação deixa de ser um
     * clique aqui para virar trabalho na tela de detalhe do protocolo.
     */
    readonly linhasPresas = computed(() => this.linhas().filter((linha) => linha.preso));

    /**
     * Parar precisa estar alcançável enquanto houver o que parar: com a fila rodando, e também com ela
     * já `PARADA` por sessão expirada — nesse caso ela ainda aceita `repetir`, e parar é o jeito de
     * dizer "não toque mais neste lote". Depois de um `parar()` do próprio analista não sobra nada a
     * parar, e depois de `ENCERRADA` a fila já esgotou sozinha. O que já foi feito não se desfaz em
     * nenhum dos casos.
     */
    readonly podeParar = computed(() => this.disparado() && !this.paradaPedidaPeloAnalista() && this.estadoDaFila() !== EEstadoDaFila.ENCERRADA);

    /**
     * Há uma chamada ao portal deste lote aberta AGORA, e por isso o diálogo não pode ser fechado.
     *
     * Correção final (Crítico 5): `fechar()` chamava `parar()` — que por contrato NÃO cancela a
     * chamada em andamento — e emitia a recarga do quadro na mesma linha síncrona. Com o item 7 de 20
     * em "Finalizando no portal", o quadro recarregava ANTES de o portal comitar: o protocolo voltava
     * à listagem como aberto, a finalização era aceita segundos depois e o quadro na tela ficava sendo
     * o retrato do instante anterior, sem recarregar de novo. O analista, vendo o card ainda lá, o
     * remarcava num lote novo — segunda finalização do mesmo protocolo. E um item que virasse "preso"
     * nunca chegaria a renderizar o alerta: o diálogo já estaria fechado.
     *
     * O diálogo irmão do mesmo módulo já resolvia isso (`kanban-protocolos.html`:
     * `[closable]="!salvando()"`); aqui o equivalente de `salvando` é o `chamadaEmVoo` da fila, lido
     * dela e não espelhado — quem enxerga o começo e o fim de cada chamada é ela.
     *
     * `disparado()` entra junto porque a fila sobrevive ao diálogo: sem ele, uma reabertura para
     * conferência ficaria intrancável por causa de uma chamada de um lote anterior.
     *
     * Adiar a emissão de `recargaDoQuadroSolicitada` até não haver chamada pendente é o mesmo fato
     * visto do outro lado: com o fechamento barrado, não existe caminho em que a recarga saia com uma
     * chamada em voo — e é por isso que não há aqui uma segunda máquina de "emitir depois".
     */
    readonly fechamentoBloqueado = computed(() => this.disparado() && this.fila.chamadaEmVoo());

    /** A fila parou antes do fim — pelo analista ou por sessão expirada. O que já foi encerrado continua encerrado. */
    readonly filaParada = computed(() => this.disparado() && this.estadoDaFila() === EEstadoDaFila.PARADA);

    /**
     * A fila parou porque a sessão caiu, e não porque alguém mandou. As duas paradas são a mesma coisa
     * para a fila e coisas MUITO diferentes para o analista: uma ele provocou; a outra significa que
     * repetir aqui só vai reproduzir o 401 até ele entrar de novo. Quem sabe qual foi é o serviço —
     * este sinal é o repasse dele, não um palpite da tela.
     */
    readonly sessaoExpirada = computed(() => this.filaParada() && this.fila.motivoDaParada() === EMotivoDaParada.SESSAO_EXPIRADA);

    /**
     * A fila parou porque a própria orquestração quebrou — nem o analista pediu, nem a sessão caiu.
     *
     * <p>Rodada de correção 2 (Menor 8): `EMotivoDaParada.FALHA_INESPERADA` existia e não mudava nada
     * — a tela mostrava a mesma frase da parada a pedido ("O lote foi interrompido"), que é falsa
     * aqui: ninguém interrompeu, e o item que estava em voo pode ter ficado sem desfecho conhecido. Um
     * valor de enum sem efeito é indistinguível de `null` para quem lê a tela, e era exatamente esse o
     * estado em que o analista mais precisava saber que o lote NÃO terminou sozinho. Agora tem frase
     * própria — e continua sendo o único dos três motivos que não bloqueia `Repetir`, porque repetir
     * é justamente o que resta a fazer.</p>
     */
    readonly falhaInesperadaNaFila = computed(() => this.filaParada() && this.fila.motivoDaParada() === EMotivoDaParada.FALHA_INESPERADA);

    /** A fila esgotou o lote sozinha. */
    readonly filaEncerrada = computed(() => this.disparado() && this.estadoDaFila() === EEstadoDaFila.ENCERRADA);

    /** O botão diz o que vai acontecer e com quantos: "Efetivar 12 protocolos" não deixa dúvida sobre o tamanho do estrago possível. */
    readonly rotuloDoConfirmar = computed(() => {
        const quantos = this.itensQueVao().length;
        const verbo = this.acaoSignal() === 'cancelar' ? 'Cancelar' : 'Efetivar';
        return `${verbo} ${quantos} ${quantos === 1 ? 'protocolo' : 'protocolos'}`;
    });

    constructor() {
        // A janela de silêncio é do injetor RAIZ e sobreviveria a este componente: sem isto, um
        // diálogo destruído com o lote em curso deixaria o app inteiro sem toast de erro nos serviços.
        inject(DestroyRef).onDestroy(() => this.silenciadorDeErros.encerrar());
    }

    /**
     * Abre a conferência. **Não dispara chamada nenhuma** — é essa ausência que faz do diálogo um
     * portão: se abrir já executasse, ele deixaria de proteger contra o engano que existe para pegar.
     *
     * `colunaDestino` é obrigatória e vem do quadro junto com a seleção: é para lá que cada protocolo
     * finalizado no portal será movido. Exigi-la aqui, e não deixá-la opcional, é o que impede um lote
     * de chegar até o botão de confirmar sem ter para onde ir.
     */
    abrir(itens: ItemStatus[], coluna: KanbanColuna, colunaDestino: KanbanColuna): void {
        this.selecaoSignal.set([...itens]);
        this.colunaSignal.set(coluna);
        this.colunaDestinoSignal.set(colunaDestino);
        this.removidasSignal.set(new Set<string>());
        this.acaoSignal.set('efetivar');
        this.motivoSignal.set('');
        this.disparadoSignal.set(false);
        this.copiaSignal.set(EResultadoDaCopia.NAO_PEDIDA);
        this.visivelSignal.set(true);
        this.carregarRetratos(itens);
    }

    /**
     * Busca os retratos do lote de uma vez, na abertura, e não linha a linha: a população é fechada e
     * pequena (o que o analista marcou), e todas as linhas ficam visíveis juntas — não há o que adiar.
     *
     * Pede pelos itens da SELEÇÃO, e não por `linhas()`: a lista exibida depende da ação escolhida, e
     * trocar de Efetivar para Cancelar não pode disparar uma segunda rodada de buscas pelos mesmos
     * rostos. O serviço ignora o que já está em cache, então repetir aqui seria inofensivo — mas
     * pedir o que já se tem é ruído que não precisa existir.
     */
    private carregarRetratos(itens: readonly ItemStatus[]): void {
        const medicos = itens
            .map((item) => separarCrmUf(crmUfDoRostoDoCard(this.tipoPessoa(), item), item.ufCrm))
            .filter((crmUf): crmUf is NonNullable<typeof crmUf> => crmUf !== null && crmUf !== undefined)
            .map((crmUf) => ({ crm: String(crmUf.crm), uf: crmUf.uf }));

        if (medicos.length) {
            this.fotosMedicos.carregar(medicos);
        }
    }

    /**
     * Manda a fila começar, com o que sobrou da conferência. Daqui em diante a mesma lista continua na
     * tela, nas mesmas posições, ganhando o estado de cada item — não há tela nova, para o analista
     * nunca precisar reencontrar onde cada protocolo foi parar.
     *
     * A guarda repete `podeConfirmar()` de propósito: o botão já respeita o sinal, mas este método é
     * público e um disparo indevido custa finalizações repetidas no portal.
     */
    confirmar(): void {
        const colunaDestino = this.colunaDestinoSignal();
        const idServico = this.idServico();
        if (!this.podeConfirmar() || !colunaDestino || idServico === undefined) {
            this.logger.warn('Confirmação do lote recusada: faltam itens elegíveis, motivo, serviço ou coluna de destino.');
            return;
        }

        const acao = this.acaoSignal();
        this.disparadoSignal.set(true);
        this.copiaSignal.set(EResultadoDaCopia.NAO_PEDIDA);
        // A partir daqui, os erros dos endpoints do lote são relatados por ESTA tela, item a item —
        // o toast global empilharia um por falha por cima dela.
        this.silenciadorDeErros.iniciar();
        this.fila.iniciar({
            itens: this.itensQueVao().map((avaliado) => avaliado.item),
            colunaDestino,
            acao,
            motivo: acao === 'cancelar' ? this.motivoSignal().trim() : undefined,
            idServico,
            codigoServico: this.codigoServico(),
            uf: this.uf()
        });
    }

    /** Interrompe o lote depois do item corrente. O que já foi encerrado continua encerrado — parar não desfaz nada. */
    parar(): void {
        this.fila.parar();
    }

    /** Refaz um item que falhou. Quem decide se é uma finalização nova ou só o movimento é a fila, que lembra o que o portal já aceitou. */
    repetir(chave: string): void {
        this.fila.repetir(chave);
    }

    /**
     * O item falhou e a fila está livre para tentar de novo. Um `parar()` do analista fecha essa porta
     * — a fila recusaria a chamada, e um botão que só produz um aviso no console é pior que botão
     * nenhum.
     */
    podeRepetir(chave: string): boolean {
        if (!this.disparado() || this.paradaPedidaPeloAnalista() || this.estadoDaFila() === EEstadoDaFila.RODANDO) {
            return false;
        }
        return this.fila.itens().some((doLote) => doLote.chave === chave && doLote.estado === EEstadoDoItemDoLote.FALHOU);
    }

    /**
     * Fecha o diálogo, manda a fila parar e — só se o lote chegou a rodar — pede a recarga do quadro.
     * `parar()` é no-op enquanto a fila só conferiu, mas o pedido é incondicional de propósito: o
     * diálogo é a única superfície que mostra o lote, e fechá-lo jamais pode deixar chamadas seguindo
     * ao portal atrás das costas do analista.
     *
     * Reentrante por desenho: com o diálogo já fechado nada aqui roda de novo. Fechar tem DUAS portas
     * — o botão e o `(visibleChange)` do `p-dialog` —, e sem esta guarda a recarga do quadro sairia
     * duas vezes se as duas dispararem, o que custaria ~20 chamadas em vez de ~10.
     *
     * Com chamada ao portal em voo, NÃO fecha e NÃO emite nada (ver `fechamentoBloqueado`). O
     * template já esconde o X, desliga o ESC e desabilita o botão; esta guarda existe porque o método
     * é público e o custo de escapar dela é uma segunda finalização sobre o mesmo protocolo. É uma
     * espera curta — o tempo de uma chamada —, e `Parar` continua alcançável durante ela.
     */
    fechar(): void {
        if (!this.visivelSignal()) {
            return;
        }
        if (this.fechamentoBloqueado()) {
            this.logger.warn('Fechamento do lote recusado: há uma chamada ao portal em andamento.');
            return;
        }

        this.fila.parar();
        this.silenciadorDeErros.encerrar();
        this.visivelSignal.set(false);

        if (this.disparado()) {
            this.recargaDoQuadroSolicitada.emit();
        }
    }

    /** `(visibleChange)` do `p-dialog`: só o fechamento interessa: quem abre é `abrir()`, com a seleção em mãos. */
    aoMudarVisibilidade(visivel: boolean): void {
        if (!visivel) {
            this.fechar();
        }
    }

    definirAcao(acao: AcaoServico): void {
        this.acaoSignal.set(acao);
    }

    definirMotivo(motivo: string): void {
        this.motivoSignal.set(motivo);
    }

    /**
     * Tira o protocolo do lote, pelo caminho da fase em que ele está: na conferência a fila ainda nem
     * foi povoada, e depois do disparo é ela quem manda — só ela sabe se aquele item já começou a ser
     * processado, e um item já em voo não sai do lote apagando a linha da tela.
     */
    remover(chave: string): void {
        if (this.disparado()) {
            this.fila.removerPendente(chave);
            return;
        }
        this.removerDaConferencia(chave);
    }

    /**
     * Copia o resultado do lote como texto: o resumo, os encerrados com pendência de cadastro e as
     * falhas com o erro real de cada uma. Quem encerrou quarenta protocolos e teve três falhas vai
     * querer anotar quais — e as pendências são a lista de trabalho do dia seguinte.
     *
     * Uma cópia só, e não uma por lista: as duas viram a mesma anotação, e dois botões obrigariam a
     * colar duas vezes para ter o quadro inteiro.
     */
    async copiarResultado(): Promise<void> {
        try {
            await this.copiarTexto(this.textoDoResultado());
            this.copiaSignal.set(EResultadoDaCopia.COPIADO);
        } catch (erro: unknown) {
            this.logger.warn('Falha ao copiar o resultado do lote para a área de transferência.', erro);
            this.copiaSignal.set(EResultadoDaCopia.FALHOU);
        }
    }

    /**
     * O texto que vai para a área de transferência — montado aqui, e não no template, para ser o mesmo
     * texto testável.
     *
     * <p>Rodada de correção 2: o cabeçalho passou a bater com as seções que vêm abaixo dele. Antes
     * dizia "N falharam" contando os presos junto (Importante 6), e chamava de "fora do lote" só quem
     * o analista tirou à mão, deixando os barrados pelo gate de fora do registro (Menor 2) — num lote
     * em que 12 caíram por permissão, o que ele colava no chamado dizia "0 fora do lote". Os dois
     * números agora aparecem, com nomes diferentes.</p>
     */
    private textoDoResultado(): string {
        const resumo = this.resumo();
        const linhas: string[] = [
            `Encerramento em lote: ${this.encerrados()} encerrados, ${resumo.comPendencias} com pendências, ${resumo.falharam} falharam, ` +
                `${resumo.presos} finalizados no portal sem movimento no quadro, ${resumo.foraDoLote} retirados do lote, ${this.foraDoLote().length} não entraram no lote.`
        ];

        if (this.linhasComPendencias().length > 0) {
            linhas.push('', 'Encerrados com pendência de cadastro:');
            for (const linha of this.linhasComPendencias()) {
                linhas.push(`#${identificadorProtocolo(linha.item)} - ${linha.item.empresa} - faltou: ${linha.pendencias.join('; ')}`);
            }
        }

        if (this.linhasPresas().length > 0) {
            // Seção própria, e a primeira das duas de falha: destas o portal JÁ aceitou a finalização,
            // e é a única lista do lote que perde o caminho curto de recuperação quando o quadro recarrega.
            linhas.push('', 'Finalizados no portal, sem movimento no quadro (recuperar pela tela de detalhe):');
            for (const linha of this.linhasPresas()) {
                linhas.push(`#${identificadorProtocolo(linha.item)} - ${linha.item.empresa} - ${linha.erro ?? 'Falha sem mensagem do backend.'}`);
            }
        }

        if (this.linhasQueFalharam().length > 0) {
            linhas.push('', 'Falharam:');
            for (const linha of this.linhasQueFalharam()) {
                linhas.push(`#${identificadorProtocolo(linha.item)} - ${linha.item.empresa} - ${linha.erro ?? 'Falha sem mensagem do backend.'}`);
            }
        }

        return linhas.join('\n');
    }

    /**
     * Sem `navigator.clipboard` a cópia falha explicitamente, e o aviso manda selecionar a lista — que
     * está na tela justamente por isso. A API é tipada como sempre presente pela `lib.dom`, mas em
     * contexto não seguro (HTTP simples) o navegador não a injeta: o tipo local reconhece a ausência
     * que o tipo global nega.
     */
    private copiarTexto(texto: string): Promise<void> {
        const areaDeTransferencia: { writeText?: (texto: string) => Promise<void> } | undefined = navigator.clipboard;
        if (!areaDeTransferencia?.writeText) {
            return Promise.reject(new Error('Área de transferência indisponível neste navegador.'));
        }
        return areaDeTransferencia.writeText(texto);
    }

    /**
     * Tira da conferência o protocolo marcado por engano. Local, e não `removerPendente` do serviço:
     * na conferência a fila ainda não foi povoada — nada existe lá para remover.
     */
    removerDaConferencia(chave: string): void {
        if (!this.conferencia().some((avaliado) => avaliado.chave === chave)) {
            this.logger.warn('Remoção pedida para um protocolo fora da conferência atual:', chave);
            return;
        }
        this.removidasSignal.update((removidas) => new Set<string>([...removidas, chave]));
    }

    textoDoMotivo(motivo: EMotivoForaDoLote): string {
        return TEXTOS_DO_MOTIVO[motivo].linha;
    }

    /** "12 de 20 fora por pagamento" — a frase que revela a causa comum de um lote que esvaziou. */
    textoDoAgregado(agregado: AgregadoForaDoLote): string {
        return `${agregado.quantidade} de ${this.totalConferido()} fora por ${TEXTOS_DO_MOTIVO[agregado.motivo].resumo}`;
    }

    identificador(item: ItemStatus): string {
        return identificadorProtocolo(item);
    }

    /**
     * Nome de quem o retrato mostra — que na pessoa jurídica NÃO é o nome exibido ao lado dele.
     *
     * Um só lugar resolve isso, e as iniciais, o rótulo e a busca da foto todos partem daqui: se cada
     * um decidisse por conta própria, uma linha poderia exibir o rosto de uma pessoa com as iniciais
     * de outra.
     */
    private nomeDoRetratado(item: ItemStatus): string | undefined {
        return rostoDoCardEhDoDiretorTecnico(this.tipoPessoa()) ? item.diretorTecnicoNome : item.empresa;
    }

    /** Retrato da linha, ou `null` enquanto não chegou (ou se não há foto cadastrada). */
    fotoDoRetratado(item: ItemStatus): string | null {
        const crmUf = separarCrmUf(crmUfDoRostoDoCard(this.tipoPessoa(), item), item.ufCrm);
        return crmUf ? this.fotosMedicos.fotoPorCrmUf(String(crmUf.crm), crmUf.uf) : null;
    }

    /**
     * Iniciais para o círculo sem foto. Médico sem retrato é o caso comum, não a exceção — e no
     * pré-prestador é o estado mais provável de todos.
     */
    iniciaisDoRetratado(item: ItemStatus): string {
        return iniciaisDoNome(this.nomeDoRetratado(item));
    }

    /**
     * Rótulo próprio do retrato, só na pessoa jurídica: ali o rosto é do diretor técnico e o nome ao
     * lado é o da empresa, então sem ele ninguém sabe de quem é aquele rosto. Vazio na física mantém o
     * retrato decorativo — anunciá-lo repetiria o nome que já se lê ao lado.
     */
    rotuloDoRetrato(item: ItemStatus): string {
        if (!rostoDoCardEhDoDiretorTecnico(this.tipoPessoa())) {
            return '';
        }
        const nome = (item.diretorTecnicoNome ?? '').trim();
        return nome ? `Diretor técnico: ${nome}` : '';
    }

    /**
     * A terceira linha do bloco: CRM/UF, ou CNPJ no pré-prestador, onde a empresa ainda não tem CRM.
     *
     * Mesma resolução do card do quadro, inclusive o `||` na UF: `crmUf` no formato `203629-` produz
     * UF vazia (não `undefined`), e com `??` o fallback da UF do quadro seria pulado e a linha sumiria
     * com a UF em mãos. Vazio faz a linha sumir, sem placeholder.
     */
    identificacaoDoSolicitante(item: ItemStatus): string {
        if (identificacaoPorCnpj(this.codigoServico())) {
            const cnpj = (item.cnpj ?? '').trim();
            return cnpj ? `CNPJ ${MascaraUtil.cpfCnpj(cnpj)}` : '';
        }

        const [crm, uf] = (item.crmUf ?? '').trim().split('-');
        const ufResolvida = (uf || item.ufCrm || this.uf() || '').trim();
        return crm && ufResolvida ? `CRM ${crm.trim()}/${ufResolvida.toUpperCase()}` : '';
    }

    /** Última tramitação conhecida do protocolo — ver o doc-comment da coluna no template. */
    ultimaTramitacao(item: ItemStatus): string {
        return formatarDataTramitacao(item.ultimaTramitacao?.dataTramitacao);
    }

    /**
     * A elegibilidade **do lote**, que não é a da tela unitária. Aplica todos os gates de domínio de
     * `podeEfetivarProtocolo`/`podeCancelarProtocolo` do quadro — identidade do protocolo, UF, roles e,
     * na efetivação, pagamento — e troca UM deles: o gate de tela vira
     * `efetivacaoSoNaTelaDeDetalheNoLote`, que libera só os dois serviços da lista opt-in
     * (ver `servicos-acoes.utils.ts`, onde a razão de cada um está escrita ao lado do Set).
     *
     * Acrescenta um gate que a tela unitária não tem: serviço sem endpoint mapeado. Ele passa no gate
     * de roles por default (código fora de `ROLES_POR_CODIGO` mantém o default aberto) e só erra na
     * hora da chamada — no unitário é uma falha isolada; num lote de trinta, trinta falhas idênticas.
     *
     * Acrescenta um segundo (correção final, Crítico 1): **serviço cuja efetivação exige campo por
     * protocolo** (`campoDaConfirmacao(codigo) !== 'nenhum'`). O lote não coleta campo — foi decisão
     * do usuário —, e o gate de tela que já existia responde a OUTRA pergunta: `diretor-clinico` e
     * `diretor-tecnico-solicitacao-pf` exigem "Data da diretoria" e não estão em
     * `EFETIVACAO_SO_NA_TELA_DE_DETALHE`, então entravam no lote e o backend, sem `dataDiretoria`,
     * caía em `dataHora` — a data de ABERTURA do protocolo. Dezoito trocas de diretor gravadas com a
     * data de dois meses antes da plenária que as autorizou, HTTP 200 em todas, nada na lista de
     * pendências. Pelo card o mesmo analista não conseguiria: `escolhaDaConclusaoCompleta` trava o
     * botão até o campo estar preenchido.
     *
     * A checagem é a MESMA função que a tela unitária usa, e não uma segunda lista: serviço novo que
     * amanhã passe a exigir campo fica fora do lote por construção, sem depender de alguém lembrar de
     * atualizar dois lugares. Fecha junto o ruído dos cinco códigos que hoje entram e tomam 400 em
     * todos os itens por falta do mesmo campo (`cancelamento-falecimento`, `regimento-interno`,
     * `alteracao-cadastral`, `alteracao-contratual`, `visto-alteracao-contratual`).
     *
     * Devolve o PRIMEIRO motivo que derruba o item, na ordem em que o analista consegue agir sobre
     * eles: o que é do protocolo antes do que é do serviço. Quadro sem serviço (`codigoServico` vazio)
     * cai no gate de endpoint, que é a verdade literal: não há endpoint mapeado para código nenhum.
     */
    private motivoForaDoLote(item: ItemStatus, acao: AcaoServico): EMotivoForaDoLote | null {
        const codigo = this.codigoServico();
        const uf = this.ufDoProtocolo(item);

        if (!protocoloTemIdentidade(item)) {
            return EMotivoForaDoLote.SEM_IDENTIDADE;
        }
        if (acoesOcultasPorUf(codigo, uf)) {
            return EMotivoForaDoLote.ACAO_OCULTA_POR_UF;
        }
        if (acao === 'efetivar' && efetivacaoSoNaTelaDeDetalheNoLote(codigo)) {
            return EMotivoForaDoLote.SO_NA_TELA_DE_DETALHE;
        }
        // Vizinho do gate acima de propósito: os dois dizem "a efetivação DESTE serviço não cabe num
        // lote", são os dois exclusivos da efetivação, e nenhum dos dois o analista consegue
        // contornar — diferente de permissão, que ele pode pedir. Ver o doc do método.
        if (acao === 'efetivar' && campoDaConfirmacao(codigo) !== 'nenhum') {
            return EMotivoForaDoLote.EXIGE_CAMPO_NA_CONFIRMACAO;
        }
        if (!this.temPermissao(codigo, acao, uf)) {
            return EMotivoForaDoLote.SEM_PERMISSAO;
        }
        if (!this.facade.temEndpointDaAcao(acao, codigo)) {
            return EMotivoForaDoLote.SEM_ENDPOINT;
        }
        if (acao === 'efetivar' && item.inPago !== true) {
            return EMotivoForaDoLote.SEM_PAGAMENTO;
        }
        return null;
    }

    /** Mesmo fail-closed do quadro: código mapeado sem a role da ação barra; código fora do mapa mantém o default aberto. */
    private temPermissao(codigo: string, acao: AcaoServico, uf: string): boolean {
        const roles = rolesDoServico(codigo);
        if (!roles) {
            return true;
        }
        const role = acao === 'efetivar' ? roles.confirmar : roles.cancelar;
        return !!role && this.permissionService.can(role, { uf });
    }

    /** Mesmo `protocolo.ufCrm ?? uf do quadro` que o quadro e a tela de detalhe fazem antes de agir. */
    private ufDoProtocolo(item: ItemStatus): string {
        return ((item.ufCrm ?? '').trim() || this.uf().trim()).toUpperCase();
    }
}

/**
 * O mesmo teste que `arrastavel` faz no quadro: sem número/ano inteiros não há identidade do
 * protocolo no portal, e a chamada sairia com `numeroProtocolo: ''` — o portal responderia sobre
 * outra coisa. String vazia é AUSÊNCIA, nunca zero: `Number('')` vale `0` e passaria por
 * `Number.isInteger`.
 */
function protocoloTemIdentidade(item: ItemStatus): boolean {
    return ehInteiro(item.numeroProtocolo) && ehInteiro(item.anoProtocolo);
}

function ehInteiro(valor?: string): boolean {
    const texto = (valor ?? '').trim();
    return texto.length > 0 && Number.isInteger(Number(texto));
}
